import type { ForgeResult, HeatMap, StrikeResult } from '../../types';

export const OPTIMAL_HEAT_LOW = 0.7;
export const OPTIMAL_HEAT_HIGH = 0.85;
export const OPTIMAL_HEAT_CENTER = (OPTIMAL_HEAT_LOW + OPTIMAL_HEAT_HIGH) / 2;
export const BURN_THRESHOLD = 1.0;

export function average(arr: readonly number[]): number {
  if (arr.length === 0) return 0;
  let s = 0;
  for (const v of arr) s += v;
  return s / arr.length;
}

/**
 * Legacy heat score against the canonical 0.7-0.85 band.
 * Kept for tests and any single-zone fallback; the live game uses a
 * pre-computed heatScore captured at the moment the player commits.
 */
export function scoreHeat(map: HeatMap): number {
  const samples = map.samples;
  if (samples.length === 0) return 0;
  const burnPenalty = samples.some((s) => s > BURN_THRESHOLD) ? 0.3 : 0;
  const avg = average(samples);
  const distFromOptimal = Math.abs(avg - OPTIMAL_HEAT_CENTER);
  return Math.max(0, 1 - distFromOptimal * 2 - burnPenalty);
}

/**
 * Score a single zone against a (possibly drifting) target band.
 * Returns 1.0 at the band center, falling off linearly past the band edges.
 */
export function scoreHeatZone(heat: number, targetCenter: number, bandWidth: number): number {
  const halfWidth = bandWidth / 2;
  const dist = Math.abs(heat - targetCenter);
  if (dist <= halfWidth) return 1;
  // Beyond the band, each band-width of error costs 0.7.
  const overshoot = (dist - halfWidth) / Math.max(0.01, bandWidth);
  return Math.max(0, 1 - overshoot * 0.7);
}

export function scoreStrikes(strikes: readonly StrikeResult[]): number {
  if (strikes.length === 0) return 0;
  const scores = strikes.map((s) =>
    s.quality === 'perfect' ? 1.0 : s.quality === 'good' ? 0.6 : 0,
  );
  return average(scores);
}

export function calculateQuality(forge: ForgeResult): number {
  const heat = forge.heatScore;
  const strike = scoreStrikes(forge.strikes);
  const comboBonus = Math.min(0.1, forge.bestComboStreak * 0.02);
  const quenchHit = forge.quenchTotal === 0 ? 0 : forge.quenchExtinguished / forge.quenchTotal;
  const quench = Math.max(0, quenchHit - forge.quenchWarpPenalty);
  const burnPenalty = Math.min(0.6, forge.burnedFrames / 240);
  const raw = heat * 0.3 + strike * 0.5 + quench * 0.2 + comboBonus - burnPenalty;
  return Math.max(0, Math.min(1, raw));
}

export function qualityLabel(q: number): string {
  if (q >= 0.9) return 'Masterwork';
  if (q >= 0.75) return 'Excellent';
  if (q >= 0.55) return 'Good';
  if (q >= 0.35) return 'Crude';
  if (q >= 0.15) return 'Poor';
  return 'Broken';
}

/** Generate procedural damage marks from a poor forge. */
export function generateDamage(strikes: readonly StrikeResult[]): number[] {
  const damage: number[] = [];
  for (const s of strikes) {
    if (s.quality === 'miss') damage.push(s.position);
  }
  return damage;
}
